import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import Button from '../../../components/ui/Button';
import { formatNumber, usePanel } from '../../performance-operations/config';
import { Badge } from '../../performance-operations/styles';
import { ListWrap } from '../styles';
import {
  axisLabel, fetchPeople, inviteToDevelopmentPlan, selectionLabel,
} from '../config';

// Analiz E09 alt sağ — seçili hücre / geçiş / eksik eksen listesi: çalışan, rol, performans, potansiyel, kritik rol ve hazır olma.
// Liste kişi kümesinden gelir (bireysel erişim ayrı yol; toplam gizlemesi listeyi açmaz). Başlık yalnız DataTable başlığında.
// Satır işlemleri: çalışan profili (E14 yerine mevcut çalışan detayı) ve E09 → E12 "Gelişim Planına Davet Et" (TD.Write).
export default function NineBoxPeopleTable({
  filters, processes, selection, potentialVisible, canInvite, onClearSelection, refreshKey,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [paging, setPaging] = useState({ pageNumber: 1, pageSize: 10 });
  const [invite, setInvite] = useState(null);
  const [inviting, setInviting] = useState(false);
  const key = JSON.stringify({ filters, processes, selection });
  // Seçim ya da süzgeç değişince ilk sayfaya dönülür.
  const [lastKey, setLastKey] = useState(key);
  if (lastKey !== key) { setLastKey(key); setPaging((p) => ({ ...p, pageNumber: 1 })); }

  const list = usePanel(
    () => fetchPeople(filters, processes, selection, paging.pageNumber, paging.pageSize),
    [key, paging.pageNumber, paging.pageSize, refreshKey],
    !!filters.periodId,
  );
  const rows = useMemo(() => (Array.isArray(list.data) ? list.data : (list.data?.data || [])), [list.data]);
  const total = list.data?.totalRowCount ?? rows.length;

  const band = useCallback((b) => (b ? t(`performance.ninebox.${b}`, { defaultValue: b }) : '-'), [t]);

  const columns = useMemo(() => {
    const cols = [
      { name: 'fullName', label: t('talent.nineBoxTransitions.col.employee'), options: { filter: false, sort: false } },
      { name: 'titleName', label: t('talent.nineBoxTransitions.col.role'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      { name: 'departmentName', label: t('talent.nineBoxTransitions.col.department'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      {
        name: 'performanceBand', label: t('talent.nineBoxTransitions.col.performance'),
        options: {
          filter: false, sort: false,
          customBodyRenderLite: (i) => {
            const r = rows[i];
            if (!r) return '-';
            return r.performanceScore != null ? `${band(r.performanceBand)} (${formatNumber(r.performanceScore, lang)})` : band(r.performanceBand || r.resultBand);
          },
        },
      },
    ];
    if (potentialVisible) {
      cols.push(
        {
          name: 'potentialBand', label: t('talent.nineBoxTransitions.col.potential'),
          options: {
            filter: false, sort: false,
            customBodyRenderLite: (i) => {
              const r = rows[i];
              if (!r) return '-';
              return r.potentialScore != null ? `${band(r.potentialBand)} (${formatNumber(r.potentialScore, lang)})` : band(r.potentialBand);
            },
          },
        },
        { name: 'cellKey', label: t('talent.nineBoxTransitions.col.cell'), options: { filter: false, sort: false, customBodyRender: (v) => (v ? axisLabel(t, v, true) : '-') } },
        { name: 'previousCellKey', label: t('talent.nineBoxTransitions.col.previousCell'), options: { filter: false, sort: false, customBodyRender: (v) => (v ? axisLabel(t, v, true) : '-') } },
        {
          name: 'missingReason', label: t('talent.nineBoxTransitions.col.missingReason'),
          options: { filter: false, sort: false, customBodyRender: (v) => (v ? t(`talent.nineBoxTransitions.missing.${v}`) : '-') },
        },
      );
    }
    cols.push(
      {
        name: 'isCriticalIncumbent', label: t('talent.nineBoxTransitions.col.criticalRole'),
        options: { filter: false, sort: false, customBodyRender: (v) => (v ? <Badge $tone="info">{t('talent.nineBoxTransitions.criticalYes')}</Badge> : '-') },
      },
      {
        name: 'readiness', label: t('talent.nineBoxTransitions.col.readiness'),
        options: { filter: false, sort: false, customBodyRender: (v) => (v ? t(`performance.succession.readiness.${v}`, { defaultValue: v }) : '-') },
      },
      {
        name: 'employeeId', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false, viewColumns: false,
          customBodyRenderLite: (i) => {
            const r = rows[i];
            if (!r) return null;
            const actions = [{ type: 'view', label: t('talent.nineBoxTransitions.action.profile'), onClick: () => history.push(`/employees/${r.employeeId}`) }];
            if (canInvite) actions.push({ type: 'send', label: t('talent.nineBoxTransitions.action.inviteIdp'), onClick: () => setInvite(r) });
            return <OptionsMenu row={r} actions={actions} />;
          },
        },
      },
    );
    return cols;
  }, [t, lang, rows, potentialVisible, canInvite, band, history]);

  const onTableChanged = useCallback((action, state) => {
    if (action === 'changePage' || action === 'changeRowsPerPage') {
      setPaging({ pageNumber: (state.page || 0) + 1, pageSize: state.rowsPerPage || 10 });
    }
  }, []);

  const confirmInvite = async () => {
    if (!invite) return;
    setInviting(true);
    await inviteToDevelopmentPlan([invite.employeeId]);
    setInviting(false);
    setInvite(null);
  };

  return (
    <ListWrap data-panel="people">
      <DataTable
        title={selectionLabel(t, selection, potentialVisible)}
        fetching={list.loading}
        columns={columns}
        data={list.error ? [] : rows}
        totalRowCount={total}
        pageIndex={paging.pageNumber - 1}
        pageSize={paging.pageSize}
        onTableChanged={onTableChanged}
        disableFilters
        tableBodyMaxHeight="none"
        toolbarActions={selection ? (
          <Button variant="ghost" onClick={onClearSelection} data-action="clear-selection">{t('talent.nineBoxTransitions.people.clear')}</Button>
        ) : null}
      />
      {list.error ? (
        <Button variant="outline" onClick={list.retry} data-action="retry-list">{t('performance.operations.panel.retry')}</Button>
      ) : null}
      <ConfirmDialog
        isOpen={!!invite}
        type="info"
        title={t('talent.nineBoxTransitions.invite.title')}
        message={invite ? t('talent.nineBoxTransitions.invite.message', { name: invite.fullName }) : ''}
        confirmText={t('talent.nineBoxTransitions.invite.confirm')}
        onConfirm={confirmInvite}
        onCancel={() => setInvite(null)}
        loading={inviting}
      />
    </ListWrap>
  );
}
